import { PENDULUM_BALL_DIAMETER } from '../../consts/clockDimensions';
import {
  PENDULUM_BOB_DIAMETER_PX,
  PENDULUM_LOGO_SIZE_MIN,
  PENDULUM_LOGO_SIZE_MAX,
  PENDULUM_LOGO_SIZE_LEGACY_DEFAULT,
  PendulumLogoType,
} from '../../consts/pendulumLogo';

// Ratio between this component's print-oriented ball unit (PENDULUM_BALL_DIAMETER)
// and the clock app's real device-px bob diameter (PENDULUM_BOB_DIAMETER_PX) -
// these are different unit spaces, see consts/pendulumLogo.ts.
const BALL_TO_DEVICE_PX_RATIO =
  PENDULUM_BALL_DIAMETER / PENDULUM_BOB_DIAMETER_PX;

const clampPendulumLogoSize = (
  pendulumLogoSize?: number,
): number | undefined =>
  pendulumLogoSize !== undefined && Number.isFinite(pendulumLogoSize)
    ? Math.min(
        Math.max(pendulumLogoSize, PENDULUM_LOGO_SIZE_MIN),
        PENDULUM_LOGO_SIZE_MAX,
      )
    : undefined;

/**
 * One place owns "how big does the pendulum logo render", so Pendulum.tsx
 * just consumes a resolved pixel diameter instead of branching on
 * isApprovedCollection/pendulumLogoType itself.
 *
 * NOTE (pendulumLogoType 'sized', the default): the isApprovedCollection
 * branch fills the whole ball (100%), but the baked logoUrl is only a
 * pendulumLogoSize x pendulumLogoSize PNG on a 410px device bob - at the
 * 200px legacy default that's ~49% of the bob, so this preview currently
 * overstates the real device by roughly 2x. Flagged in review (klydo-studio
 * PR #522); not fixed here - needs a decision on whether klydo-api should
 * bake onto a full 410x410 canvas instead.
 *
 * pendulumLogoType 'full' (TK-688) is the exception where the 100% fill is
 * actually correct, not this same mismatch: klydo-api bakes logoUrl onto the
 * full bob canvas (cover-crop) for 'full', so an approved 'full' collection's
 * logoUrl really is a full-bob image, and a draft 'full' collection should
 * preview the same full fill ahead of that bake - hence the pendulumLogoType
 * check below applies before a collection is even approved, unlike the
 * isApprovedCollection check.
 */
export const resolveLogoDiameterPx = ({
  isApprovedCollection,
  pendulumLogoSize,
  pendulumLogoType,
  scale = 1,
}: {
  isApprovedCollection?: boolean;
  pendulumLogoSize?: number;
  pendulumLogoType?: PendulumLogoType;
  scale?: number;
}): number =>
  isApprovedCollection || pendulumLogoType === 'full'
    ? PENDULUM_BALL_DIAMETER * scale
    : (clampPendulumLogoSize(pendulumLogoSize) ??
        PENDULUM_LOGO_SIZE_LEGACY_DEFAULT) *
      BALL_TO_DEVICE_PX_RATIO *
      scale;
